import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { ApiError } from '@/lib/api';
import { showApiError } from '@/lib/errors';
import { getAiAnalyticsBaseUrl } from '@/lib/api/aiAnalyticsUrl';
import { getOrganizationId } from '@/lib/api/organization';

const AI_AGENT_BASE_URL = getAiAnalyticsBaseUrl('api');

export interface AiAgent {
  id: string;
  business_account_id: string;
  agent_name: string;
  website_url?: string | null;
  qa_data: any[];
  document_paths: string[];
  suggested_buttons: any[];
  suggested_questions: any[];
  connect: boolean;
  connected_channel?: string | null;
  whatsapp_connected: boolean;
  whatsapp_phone_number_id?: string | null;
  whatsapp_phone_display?: string | null;
  whatsapp_flow_id?: string | null;
  whatsapp_flow_name?: string | null;
  created_at: string;
  updated_at: string;
}

interface AiAgentsResponse {
  business_account_id: string;
  agents: AiAgent[];
  count: number;
  success: boolean;
}

type AiAgentsLike =
  | AiAgentsResponse
  | { data?: AiAgentsResponse | AiAgent[]; agents?: AiAgent[] }
  | AiAgent[];

interface CreateAiAgentPayload {
  business_account_id: string;
  agent_name: string;
}

interface CreateAiAgentResponse {
  agent: AiAgent;
  success: boolean;
  message?: string;
}

export interface UpdateAiAgentData {
  agent_name?: string;
  website_url?: string | null;
}

export interface UpdateAiAgentResponse {
  agent: AiAgent;
  success: boolean;
  message?: string;
}

export interface DeleteAiAgentResponse {
  success: boolean;
  message?: string;
}

const apiClient = {
  async get<T>(endpoint: string): Promise<{ data: T | null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${AI_AGENT_BASE_URL}${endpoint}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        return { data: null, error: errorData.error || `HTTP ${response.status}` };
      }

      const data = await response.json();
      return { data: data.data || data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  },

  async post<T>(endpoint: string, body: any): Promise<{ data: T | null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${AI_AGENT_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        return { data: null, error: errorData.error || `HTTP ${response.status}` };
      }

      const data = await response.json();
      return { data: data.data || data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  },

  async postForm<T>(endpoint: string, formData: FormData): Promise<{ data: T | null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${AI_AGENT_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          // Let the browser set Content-Type for multipart/form-data
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        return { data: null, error: errorData.error || `HTTP ${response.status}` };
      }

      const data = await response.json();
      return { data: data.data || data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  },

  async putForm<T>(endpoint: string, formData: FormData): Promise<{ data: T | null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${AI_AGENT_BASE_URL}${endpoint}`, {
        method: 'PUT',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        return { data: null, error: errorData.error || `HTTP ${response.status}` };
      }

      const data = await response.json();
      return { data: data.data || data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  },

  async putJson<T>(endpoint: string, body: any): Promise<{ data: T | null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${AI_AGENT_BASE_URL}${endpoint}`, {
        method: 'PUT',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        return { data: null, error: errorData.error || `HTTP ${response.status}` };
      }

      const data = await response.json();
      return { data: data.data || data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  },

  async postFormUrlEncoded<T>(endpoint: string, data: Record<string, any>): Promise<{ data: T | null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      const formBody = new URLSearchParams();
      
      // Convert data to URL-encoded format
      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value) || typeof value === 'object') {
          formBody.append(key, JSON.stringify(value));
        } else {
          formBody.append(key, String(value));
        }
      });

      const response = await fetch(`${AI_AGENT_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        return { data: null, error: errorData.error || `HTTP ${response.status}` };
      }

      const responseData = await response.json();
      return { data: responseData.data || responseData, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  },

  async putFormUrlEncoded<T>(endpoint: string, data: Record<string, any>): Promise<{ data: T | null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      const formBody = new URLSearchParams();
      
      // Convert data to URL-encoded format
      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value) || typeof value === 'object') {
          formBody.append(key, JSON.stringify(value));
        } else {
          formBody.append(key, String(value));
        }
      });

      const response = await fetch(`${AI_AGENT_BASE_URL}${endpoint}`, {
        method: 'PUT',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        return { data: null, error: errorData.error || `HTTP ${response.status}` };
      }

      const responseData = await response.json();
      return { data: responseData.data || responseData, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  },

  async delete<T>(endpoint: string): Promise<{ data: T | null; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${AI_AGENT_BASE_URL}${endpoint}`, {
        method: 'DELETE',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        return { data: null, error: errorData.error || `HTTP ${response.status}` };
      }

      const data = await response.json();
      return { data: data.data || data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  },
};

interface AiAgentDetailResponse {
  agent: AiAgent;
  success: boolean;
}

export interface WebsiteProcessResponse {
  agent_id: string;
  website_url: string;
  content: string;
  key_topics: string[];
  suggested_buttons: { label: string; action: string }[];
  suggested_questions: string[];
  crawl_info: {
    pages_crawled: number;
    images_processed: number;
  };
  token_usage: {
    model: string;
    total_tokens: number;
  };
  success: boolean;
}

export interface QaPair {
  id?: string;
  question: string;
  answer: string;
}

const getStoredOrganizationId = (): string | undefined => getOrganizationId() || undefined;

const normalizeAgents = (payload: AiAgentsLike | null | undefined): AiAgent[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray((payload as any).agents)) return (payload as any).agents;
  if (Array.isArray((payload as any).data)) return (payload as any).data;
  if (Array.isArray((payload as any).data?.agents)) return (payload as any).data.agents;
  return [];
};

export function useAiAgents(businessAccountId?: string, organizationId?: string) {
  return useQuery({
    queryKey: ['ai-agents', businessAccountId, organizationId],
    queryFn: async () => {
      const resolvedOrganizationId = organizationId || getStoredOrganizationId();

      if (!businessAccountId && !resolvedOrganizationId) return [];

      if (businessAccountId) {
        const result = await apiClient.get<AiAgentsLike>(`/ai-agent/business/${businessAccountId}/agents`);
        if (!result.error) {
          return normalizeAgents(result.data);
        }
      }

      if (!resolvedOrganizationId) return [];

      const result = await apiClient.get<AiAgentsLike>(
        `/ai-agent/organization/${encodeURIComponent(resolvedOrganizationId)}/agents`
      );
      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return normalizeAgents(result.data);
    },
    enabled: !!businessAccountId || !!organizationId || !!getStoredOrganizationId(),
  });
}

export function useCreateAiAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateAiAgentPayload): Promise<CreateAiAgentResponse> => {
      const formData = new FormData();
      formData.append('business_account_id', payload.business_account_id);
      formData.append('agent_name', payload.agent_name);

      const result = await apiClient.postForm<CreateAiAgentResponse>('/ai-agent/create-name', formData);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      if (!result.data) {
        throw new Error('No data returned from API');
      }

      return result.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ai-agents', variables.business_account_id] });
      toast({ variant: "success",
        title: 'Agent created successfully',
        description: data.message || `AI Agent "${data.agent.agent_name}" has been created.`,
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to create agent' } });
    },
  });
}

export function useAiAgent(agentId?: string) {
  return useQuery({
    queryKey: ['ai-agent', agentId],
    queryFn: async () => {
      if (!agentId) return null;

      const result = await apiClient.get<AiAgentDetailResponse | AiAgent>(`/ai-agent/${agentId}`);
      if (result.error) {
        throw ApiError.fromResult(result);
      }

      const data = result.data;
      if (!data) return null;

      // Backend may return { agent: {...} } or just the agent object itself
      if ((data as AiAgentDetailResponse).agent) {
        return (data as AiAgentDetailResponse).agent;
      }

      return data as AiAgent;
    },
    enabled: !!agentId,
  });
}

export function useProcessWebsite(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { website_url: string; crawl_limit?: number }): Promise<WebsiteProcessResponse> => {
      const formData = new FormData();
      formData.append('agent_id', agentId);
      formData.append('website_url', params.website_url);
      formData.append('crawl_limit', String(params.crawl_limit ?? 5));

      const result = await apiClient.postForm<WebsiteProcessResponse>('/ai-agent/process-website', formData);
      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to process website');
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      toast({
        title: 'Website processed',
        description: 'The website content has been processed for this agent.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to process website' } });
    },
  });
}

export function useUpdateWebsite(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { website_url: string; crawl_limit?: number }): Promise<WebsiteProcessResponse> => {
      const formData = new FormData();
      formData.append('website_url', params.website_url);
      formData.append('crawl_limit', String(params.crawl_limit ?? 5));

      const result = await apiClient.putForm<WebsiteProcessResponse>(`/ai-agent/${agentId}/website`, formData);
      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to update website');
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      toast({ variant: "success",
        title: 'Website updated',
        description: 'The website for this agent has been updated.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to update website' } });
    },
  });
}

export function useDeleteWebsite(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<{ success: boolean; message?: string }> => {
      const result = await apiClient.delete<{ success: boolean; message?: string }>(
        `/ai-agent/${agentId}/website`
      );
      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to delete website');
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      toast({ variant: "success",
        title: 'Website deleted',
        description: 'The website has been removed from this agent.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to delete website' } });
    },
  });
}

export interface QaUpdateResponse {
  agent: AiAgent;
  success: boolean;
  message?: string;
}

export function useAddQa(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (qaData: QaPair[]): Promise<QaUpdateResponse> => {
      // Try URL-encoded form data (some FastAPI backends prefer this)
      const result = await apiClient.postFormUrlEncoded<QaUpdateResponse>('/ai-agent/add-qa', {
        agent_id: agentId,
        qa_data: qaData,
      });
      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to add Q&A');
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      toast({ variant: "success",
        title: 'Q&A added',
        description: 'New Q&A pair has been added for this agent.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to add Q&A' } });
    },
  });
}

export function useDeleteQa(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (qaId: string): Promise<QaUpdateResponse> => {
      const result = await apiClient.postFormUrlEncoded<QaUpdateResponse>('/ai-agent/delete-qa', {
        agent_id: agentId,
        qa_id: qaId,
      });
      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to delete Q&A');
      }
      return result.data;
    },
    onSuccess: (data) => {
      if (data && data.agent) {
        queryClient.setQueryData(['ai-agent', agentId], data.agent);
      }
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      toast({ variant: "success",
        title: 'Q&A deleted',
        description: 'Q&A pair has been deleted successfully.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to delete Q&A' } });
    },
  });
}

export function useSaveQa(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (qaData: QaPair[]): Promise<QaUpdateResponse> => {
      // Backend expects qa_data as form/multipart (see curl example), not pure JSON
      // Equivalent to:
      // curl -X PUT '{BASE_URL}/api/ai-agent/{agentId}/qa' --form 'qa_data="[...]"'
      const token = localStorage.getItem('auth_token');

      const formData = new FormData();
      formData.append('qa_data', JSON.stringify(qaData));

      try {
        const response = await fetch(`${AI_AGENT_BASE_URL}/ai-agent/${agentId}/qa`, {
          method: 'PUT',
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            // NOTE: Do NOT set Content-Type here; the browser will set the correct multipart boundary
          },
          body: formData,
        });

        const raw = await response.json().catch(() => null);

        if (!response.ok || !raw) {
          throw ApiError.fromResult(raw, `Failed to update Q&A (HTTP ${response.status})`);
        }

        // Normalise to QaUpdateResponse shape
        const data: QaUpdateResponse =
          raw.agent || raw.success !== undefined
            ? {
                agent: (raw.agent ?? raw) as AiAgent,
                success: Boolean(raw.success ?? true),
                message: raw.message,
              }
            : raw;

        if (!data.agent) {
          throw new Error('Invalid response from Q&A update API');
        }

        return data;
      } catch (error) {
        if (error instanceof Error) {
          throw error;
        }
        throw new Error('Failed to update Q&A');
      }
    },
    onSuccess: (data) => {
      // Update the query cache directly with the new data
      if (data && data.agent) {
        queryClient.setQueryData(['ai-agent', agentId], data.agent);
      }
      // Also invalidate to trigger a refetch as backup
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      toast({ variant: "success",
        title: 'Q&A updated',
        description: 'Q&A pairs have been updated for this agent.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to update Q&A' } });
    },
  });
}

export interface AgentQueryResponse {
  agent_id: string;
  query: string;
  response: string;
  source: string;
  confidence: number;
  success: boolean;
  link_buttons?: Array<{ label: string; url: string }>;
}

export interface AgentQueryPayload {
  agent_id: string;
  query: string;
  business_account_id: string;
}

export interface DocumentsResponse {
  agent_id: string;
  document_ids: string[];
  document_paths: string[];
  success: boolean;
  message?: string;
}

export function useAgentQuery(agentId: string, businessAccountId?: string) {
  return useMutation({
    mutationFn: async (query: string): Promise<AgentQueryResponse> => {
      if (!businessAccountId) {
        throw new Error('Business account ID is required');
      }

      const result = await apiClient.post<AgentQueryResponse>('/ai-agent/query', {
        agent_id: agentId,
        query: query.trim(),
        business_account_id: businessAccountId,
      });

      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to query agent');
      }

      return result.data;
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to query agent' } });
    },
  });
}

export function useAddDocuments(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (files: File[]): Promise<DocumentsResponse> => {
      const formData = new FormData();
      formData.append('agent_id', agentId);
      files.forEach((file) => {
        formData.append('documents', file);
      });

      const result = await apiClient.postForm<DocumentsResponse>('/ai-agent/add-documents', formData);
      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to add documents');
      }

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      toast({ variant: "success",
        title: 'Documents added',
        description: 'Documents have been uploaded for this agent.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to add documents' } });
    },
  });
}

export function useUpdateDocuments(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (files: File[]): Promise<DocumentsResponse> => {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('documents', file);
      });

      const result = await apiClient.putForm<DocumentsResponse>(`/ai-agent/${agentId}/documents`, formData);
      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to update documents');
      }

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      toast({ variant: "success",
        title: 'Documents updated',
        description: 'Documents have been updated for this agent.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to update documents' } });
    },
  });
}

export interface ConnectAgentResponse {
  agent_id: string;
  channel_id: string;
  connected: boolean;
  success: boolean;
  message: string;
}

export interface DisconnectAgentResponse {
  agent_id: string;
  disconnected: boolean;
  success: boolean;
  message: string;
}

export function useConnectAgent(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (channelId: string): Promise<ConnectAgentResponse> => {
      const result = await apiClient.post<ConnectAgentResponse>('/ai-agent/connect', {
        agent_id: agentId,
        channel_id: channelId,
      });

      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to connect agent');
      }

      return result.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      queryClient.invalidateQueries({ queryKey: ['live-chat-scripts'] });
      toast({ variant: "success",
        title: 'Agent connected',
        description: data.message || 'Agent has been connected to the channel successfully.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to connect agent' } });
    },
  });
}

export function useDisconnectAgent(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<DisconnectAgentResponse> => {
      const result = await apiClient.post<DisconnectAgentResponse>('/ai-agent/disconnect', {
        agent_id: agentId,
      });

      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to disconnect agent');
      }

      return result.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      queryClient.invalidateQueries({ queryKey: ['live-chat-scripts'] });
      toast({ variant: "success",
        title: 'Agent disconnected',
        description: data.message || 'Agent has been disconnected from the channel successfully.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to disconnect agent' } });
    },
  });
}

// WhatsApp Connect/Disconnect

export interface ConnectWhatsAppResponse {
  agent_id: string;
  phone_number_id: string;
  phone_display?: string;
  connected: boolean;
  success: boolean;
  message: string;
}

export interface DisconnectWhatsAppResponse {
  agent_id: string;
  disconnected: boolean;
  success: boolean;
  message: string;
}

export function useConnectWhatsApp(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { flow_id: string; flow_name: string; phone_number_id?: string; phone_display?: string }): Promise<ConnectWhatsAppResponse> => {
      const result = await apiClient.post<ConnectWhatsAppResponse>('/ai-agent/connect-whatsapp', {
        agent_id: agentId,
        flow_id: params.flow_id,
        flow_name: params.flow_name,
        phone_number_id: params.phone_number_id || '',
        phone_display: params.phone_display || '',
      });

      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to connect WhatsApp');
      }

      return result.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      toast({ variant: "success",
        title: 'WhatsApp connected',
        description: data.message || 'Agent has been connected to WhatsApp successfully.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to connect WhatsApp' } });
    },
  });
}

export function useDisconnectWhatsApp(agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<DisconnectWhatsAppResponse> => {
      const result = await apiClient.post<DisconnectWhatsAppResponse>('/ai-agent/disconnect-whatsapp', {
        agent_id: agentId,
      });

      if (result.error || !result.data) {
        throw ApiError.fromResult(result, 'Failed to disconnect WhatsApp');
      }

      return result.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      toast({ variant: "success",
        title: 'WhatsApp disconnected',
        description: data.message || 'Agent has been disconnected from WhatsApp.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Failed to disconnect WhatsApp' } });
    },
  });
}

// Edit AI Agent Hook
export function useUpdateAiAgent(agentId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateAiAgentData): Promise<UpdateAiAgentResponse> => {
      if (!agentId) {
        throw new Error('Agent ID is required');
      }

      const formData = new FormData();
      
      if (data.agent_name) formData.append('agent_name', data.agent_name);
      if (data.website_url !== undefined) formData.append('website_url', data.website_url ?? '');

      const result = await apiClient.putForm<UpdateAiAgentResponse>(`/ai-agent/${agentId}/update`, formData);
      
      if (result.error) {
        throw ApiError.fromResult(result);
      }

      if (!result.data) {
        throw new Error('No data returned from API');
      }

      return result.data;
    },
    onSuccess: (data) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      queryClient.invalidateQueries({ queryKey: ['ai-agents'] });
      
      toast({ variant: "success",
        title: 'Agent updated',
        description: 'AI agent has been updated successfully.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Update failed' } });
    },
  });
}

// Delete AI Agent Hook
export function useDeleteAiAgent(agentId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<DeleteAiAgentResponse> => {
      if (!agentId) {
        throw new Error('Agent ID is required');
      }

      const result = await apiClient.delete<DeleteAiAgentResponse>(`/ai-agent/${agentId}/delete`);
      
      if (result.error) {
        throw ApiError.fromResult(result);
      }

      if (!result.data) {
        throw new Error('No data returned from API');
      }

      return result.data;
    },
    onSuccess: (data) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['ai-agent', agentId] });
      queryClient.invalidateQueries({ queryKey: ['ai-agents'] });
      
      toast({ variant: "success",
        title: 'Agent deleted',
        description: 'AI agent has been deleted successfully.',
      });
    },
    onError: (error: Error) => {
      showApiError(error, { fallback: { title: 'Delete failed' } });
    },
  });
}
